import { attachmentHeaders } from './download'

/**
 * Save a file to disk.
 *
 * `<a download>` is not enough and is why the Download buttons in the media
 * viewers did nothing: the attribute is honoured only for same-origin URLs, and
 * shared media is served from the storage bucket. Cross-origin the browser
 * ignores it and either navigates or, on a detached anchor, does nothing at all
 * — which is what a user sees as "the button is dead".
 *
 * Fetching the bytes and saving a blob sidesteps the rule, because the blob IS
 * same-origin. If the fetch is refused — a bucket without CORS, an expired
 * link — opening it in a tab is still better than silence.
 */
export async function downloadFile(url: string, filename?: string): Promise<void> {
  if (!url) return
  const name = filename?.trim() || 'download'
  try {
    const res = await fetch(url, { headers: attachmentHeaders(url) })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    try {
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = name
      // Firefox will not act on a click for an anchor outside the document.
      document.body.appendChild(a)
      a.click()
      a.remove()
    } finally {
      // The save has already started; the handle can go.
      URL.revokeObjectURL(objectUrl)
    }
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}
